// src/admin/pages/Orders.tsx

import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Search, Filter, Calendar, Download } from 'lucide-react';
import OrderDetailsModal from '../components/OrderDetailsModal';
import axios from 'axios';
import Papa from 'papaparse';
import { adminApi } from '../../utils/api';
import { Order } from '../../types';
import { toast } from 'react-hot-toast';

export const Orders = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch orders from backend
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await adminApi.orders.getAll();
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
        toast.error('Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Filter orders based on search, status, and date
  useEffect(() => {
    if (!orders) return;

    const filtered = orders.filter(order => {
      const matchesSearch =
        (order.order_number?.toString().toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (order.customer_name?.toLowerCase() || '').includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || order.shipping_status === statusFilter;

      let matchesDate = true;
      if (order.created_at) {
        const orderDate = new Date(order.created_at);
        const today = new Date();
        const startOfWeek = new Date();
        startOfWeek.setDate(today.getDate() - today.getDay());
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        switch (dateRange) {
          case 'today':
            matchesDate = orderDate.toDateString() === today.toDateString();
            break;
          case 'week':
            matchesDate = orderDate >= startOfWeek;
            break;
          case 'month':
            matchesDate = orderDate >= startOfMonth;
            break;
          case 'custom':
            // Implement custom range selection if needed
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    setFilteredOrders(filtered);
  }, [searchQuery, statusFilter, dateRange, orders]);

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-600';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'failed':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getShippingStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-600';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'processing':
        return 'bg-blue-100 text-blue-600';
      case 'cancelled':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const openOrderDetails = (orderId: string) => {
    setSelectedOrder(orderId);
    setIsModalOpen(true);
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
    setIsModalOpen(false);
  };

  const handlePaymentStatusChange = async (orderId: string, newStatus: 'pending' | 'paid' | 'failed') => {
    try {
      await adminApi.orders.updatePaymentStatus(orderId, newStatus);
      // Update payment status in local state
      setOrders(prevOrders =>
        prevOrders.map(order => (
          order.order_number === orderId ? { ...order, payment_status: newStatus } : order
        ))
      );
    } catch (err) {
      console.error('Error updating payment status:', err);
      toast.error('Failed to update payment status.');
    }
  };

  const handleShippingStatusChange = async (orderId: string, newStatus: 'pending' | 'processing' | 'completed' | 'cancelled') => {
    try {
      await adminApi.orders.updateShippingStatus(orderId, newStatus);
      // Update shipping status in local state
      setOrders(prevOrders =>
        prevOrders.map(order => (
          order.order_number === orderId ? { ...order, shipping_status: newStatus } : order
        ))
      );
    } catch (err) {
      console.error('Error updating shipping status:', err);
      toast.error('Failed to update shipping status.');
    }
  };

  const handleExport = () => {
    try {
      const csv = Papa.unparse(filteredOrders);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'orders.csv');
      link.click();
    } catch (err) {
      console.error('Error exporting orders:', err);
      alert('Failed to export orders.');
    }
  };

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        <div className="flex-none p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Orders</h2>
            <button
              className="bg-pink-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-pink-600 transition-colors"
              onClick={handleExport}
            >
              <Download className="h-5 w-5" />
              Export Orders
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 border-none focus:ring-0"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border">
              <Calendar className="h-5 w-5 text-gray-400" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="flex-1 border-none focus:ring-0"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto px-6 pb-6">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <svg
                  className="animate-spin h-8 w-8 text-pink-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="flex justify-center items-center py-10">
                <p className="text-gray-500">No orders found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Order No.</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Shipping Status</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {order.order_number}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {order.customer_name}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <select
                            value={order.payment_status}
                            onChange={(e) => handlePaymentStatusChange(order.order_number, e.target.value as 'pending' | 'paid' | 'failed')}
                            className={`px-2 py-1 text-xs rounded-full border-none focus:ring-0 ${getPaymentStatusColor(order.payment_status)}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="failed">Failed</option>
                            <option value="paid">Paid</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <select
                            value={order.shipping_status}
                            onChange={(e) => handleShippingStatusChange(order.order_number, e.target.value as 'pending' | 'processing' | 'completed' | 'cancelled')}
                            className={`px-2 py-1 text-xs rounded-full border-none focus:ring-0 ${getShippingStatusColor(order.shipping_status)}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs">
                          <button
                            onClick={() => openOrderDetails(order.order_number)}
                            className="text-pink-600 hover:text-pink-800 text-xs font-medium"
                          >
                            View Details
                          </button>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {order.itemsDetails?.map(item => (
                            <div key={item.id} className="flex items-center space-x-2">
                              <span>{item.product_name}</span>
                              <span className="text-gray-400">x{item.quantity}</span>
                            </div>
                          ))}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                          {order.payment_method}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Order Details Modal */}
        {isModalOpen && selectedOrder && (
          <OrderDetailsModal
            orderId={selectedOrder}
            onClose={closeOrderDetails}
          />
        )}
      </div>
    </AdminLayout>
  );
};
